import type { TFunction } from 'i18next'

import { DURATION, type Duration, ItemPaymentType, ItemPriceType } from '../../consts/order.js'

export type SubscriptionPlan = {
  resID: string
  catalog?: number
  deleted?: boolean
  domain?: number
  paymentType: number
  prices?: readonly {
    priceCent: number
    type: number
  }[]
}

export type SubscriptionMachine = {
  domains?: readonly number[]
  items?: readonly SubscriptionPlan[]
}

export type SubscriptionCreditDecision =
  | 'eligible'
  | 'insufficient_can_recharge'
  | 'insufficient_cannot_recharge'
  | 'unavailable'

export function getSubscriptionPaymentType(duration: Duration): ItemPaymentType {
  return duration === DURATION.MONTHLY
    ? ItemPaymentType.Available30Days
    : ItemPaymentType.Available1Days
}

export function getSubscriptionQuantity(duration: Duration, totalDays?: number): number {
  if (!Number.isFinite(totalDays) || !totalDays || totalDays <= 0) return 0

  return duration === DURATION.MONTHLY ? Math.ceil(totalDays / 30) : Math.trunc(totalDays)
}

export function findSubscriptionPlan<T extends SubscriptionPlan>(
  items: readonly T[] | undefined,
  duration: Duration,
): T | undefined {
  const paymentType = getSubscriptionPaymentType(duration)
  return items?.find((item) => item.paymentType === paymentType)
}

export function getPlanPriceByType(
  plan: Pick<SubscriptionPlan, 'prices'> | undefined,
  priceType: ItemPriceType,
): number | undefined {
  const priceCent = plan?.prices?.find((price) => price.type === priceType)?.priceCent
  return getValidPriceCent(priceCent)
}

export function getValidPriceCent(priceCent?: number): number | undefined {
  return Number.isFinite(priceCent) && priceCent !== undefined && priceCent >= 0
    ? priceCent
    : undefined
}

export function getSubscriptionPriceCent({
  plan,
  duration,
  totalDays,
  count = 1,
  priceType = ItemPriceType.Original,
}: {
  plan: Pick<SubscriptionPlan, 'prices'> | undefined
  duration: Duration
  totalDays?: number
  count?: number
  priceType?: ItemPriceType
}): number | undefined {
  const unitPriceCent = getPlanPriceByType(plan, priceType)
  const quantity = getSubscriptionQuantity(duration, totalDays)

  if (unitPriceCent === undefined || quantity <= 0 || !Number.isFinite(count) || count <= 0) {
    return undefined
  }

  return unitPriceCent * quantity * count
}

export function hasSufficientRechargeCredits(
  requiredCreditCent?: number,
  availableCreditCent?: number,
): boolean {
  return (
    requiredCreditCent !== undefined &&
    availableCreditCent !== undefined &&
    Number.isFinite(requiredCreditCent) &&
    Number.isFinite(availableCreditCent) &&
    requiredCreditCent >= 0 &&
    availableCreditCent >= requiredCreditCent
  )
}

export function getSubscriptionCreditDecision(
  requiredCreditCent: number | undefined,
  availableCreditCent: number | undefined,
  canRecharge: boolean,
): SubscriptionCreditDecision {
  const hasAvailableCreditData =
    requiredCreditCent !== undefined &&
    availableCreditCent !== undefined &&
    Number.isFinite(requiredCreditCent) &&
    Number.isFinite(availableCreditCent) &&
    requiredCreditCent >= 0 &&
    availableCreditCent >= 0

  if (!hasAvailableCreditData) return 'unavailable'

  if (availableCreditCent >= requiredCreditCent) return 'eligible'

  return canRecharge ? 'insufficient_can_recharge' : 'insufficient_cannot_recharge'
}

export function supportsSubscription(
  machine: SubscriptionMachine | undefined,
  duration: Duration,
): boolean {
  return findSubscriptionPlan(machine?.items, duration) !== undefined
}

export function formatSubscriptionDuration(totalDays?: number, t?: TFunction): string {
  if (!Number.isFinite(totalDays) || !totalDays || totalDays <= 0) return ''

  const wholeDays = Math.trunc(totalDays)
  const months = Math.floor(wholeDays / 30)
  const days = wholeDays % 30
  const parts: string[] = []

  if (months > 0) {
    const key = months > 1 ? 'create.PurchaseForSection.months' : 'create.PurchaseForSection.month'
    parts.push(`${months} ${t ? t(key) : `Month${months > 1 ? 's' : ''}`}`)
  }

  if (days > 0) {
    const key = days > 1 ? 'create.PurchaseForSection.days' : 'create.PurchaseForSection.day'
    parts.push(`${days} ${t ? t(key) : `Day${days > 1 ? 's' : ''}`}`)
  }

  return parts.join(' ')
}
